import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CreateActivityDialog } from "@/components/create-activity-dialog";
import { ContactForm } from "@/components/contact-form";
import {
  Zap,
  Activity,
  UserPlus,
  ExternalLink,
} from "lucide-react";

type ExternalLinkItem = {
  id: string;
  label: string;
  url: string;
  description?: string | null;
  isActive: boolean;
  openInIframe?: boolean;
};

function ExternalLinksDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);

  const { data: externalLinks } = useQuery<ExternalLinkItem[]>({
    queryKey: ["/api/screen-pop-external-links"],
    enabled: open,
  });

  const activeLinks = (externalLinks || []).filter((l) => l.isActive);
  const activeLink = activeLinks.find((l) => l.id === selectedLinkId) || activeLinks[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>External Links</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col flex-1 min-h-0">
          {activeLinks.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <ExternalLink className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No external links configured
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col flex-1 min-h-0 gap-3">
              {activeLinks.length > 1 && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm text-muted-foreground">Link:</span>
                  <Select
                    value={activeLink?.id || ""}
                    onValueChange={(v) => setSelectedLinkId(v)}
                  >
                    <SelectTrigger className="w-[260px]" data-testid="select-external-link-qa">
                      <SelectValue placeholder="Select a link" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeLinks.map((l) => (
                        <SelectItem key={l.id} value={l.id} data-testid={`select-external-link-qa-${l.id}`}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <a
                    href={activeLink?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto"
                    data-testid="button-open-link-new-tab-qa"
                  >
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              )}
              {activeLink?.openInIframe !== false ? (
                <div className="flex-1 min-h-0 rounded-md border overflow-hidden" data-testid="iframe-container-qa">
                  <iframe
                    key={activeLink?.id}
                    src={activeLink?.url}
                    className="w-full h-full border-0"
                    title={activeLink?.label}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    data-testid={`iframe-external-link-qa-${activeLink?.id}`}
                  />
                </div>
              ) : (
                <Card data-testid={`external-link-card-qa-${activeLink?.id}`}>
                  <CardContent className="py-3 px-4">
                    <a
                      href={activeLink?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 group"
                      data-testid={`external-link-qa-${activeLink?.id}`}
                    >
                      <div className="p-2 rounded-md bg-primary/10 flex-shrink-0">
                        <ExternalLink className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium group-hover:text-primary transition-colors">{activeLink?.label}</p>
                        {activeLink?.description && (
                          <p className="text-xs text-muted-foreground truncate">{activeLink?.description}</p>
                        )}
                        <p className="text-xs text-blue-500 group-hover:underline truncate">{activeLink?.url}</p>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary flex-shrink-0" />
                    </a>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function QuickActionsHeaderButton() {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [externalLinksDialogOpen, setExternalLinksDialogOpen] = useState(false);

  const menuItems = [
    {
      label: "Create Activity",
      icon: Activity,
      onClick: () => {
        setPopoverOpen(false);
        setActivityDialogOpen(true);
      },
      testId: "button-quick-create-activity",
    },
    {
      label: "Create Contact",
      icon: UserPlus,
      onClick: () => {
        setPopoverOpen(false);
        setContactDialogOpen(true);
      },
      testId: "button-quick-create-contact",
    },
    {
      label: "External Links",
      icon: ExternalLink,
      onClick: () => {
        setPopoverOpen(false);
        setExternalLinksDialogOpen(true);
      },
      testId: "button-quick-external-links",
    },
  ];

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            data-testid="button-quick-actions"
            title="Quick Actions"
          >
            <Zap className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="end">
          <div className="flex flex-col">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  data-testid={item.testId}
                  className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-left w-full"
                >
                  <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      <CreateActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
      />

      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Contact</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-1">
            <ContactForm
              onSuccess={() => setContactDialogOpen(false)}
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <ExternalLinksDialog
        open={externalLinksDialogOpen}
        onOpenChange={setExternalLinksDialogOpen}
      />
    </>
  );
}
